import type { ReactNode } from "react"

import { AlertRuleValueUnitInput } from "./AlertRuleFormFields"
import { AlertRuleDraft, AlertRuleVariant } from "./types"

interface AlertRuleModalConfig {
  title: string
  initialDraft: AlertRuleDraft
  renderSentence: (
    draft: AlertRuleDraft,
    updateDraft: (patch: Partial<AlertRuleDraft>) => void,
    thresholdError?: string,
  ) => ReactNode
  info?: string[]
}

const commonThresholdDescription =
  "Send notification (In-app and/or email) to contractor when the following threshold triggers."

const formatNumber = (value: number) => String(Math.round(value))

const formatTemperatureValue = (value: number, unit: string) =>
  unit === "C" ? String(Number(value.toFixed(2))) : formatNumber(value)

export const getTemperatureRange = (unit: string | undefined) =>
  unit === "C" ? { min: 0, max: 37.77 } : { min: 32, max: 100 }

const convertTemperatureUnit = (value: string, currentUnit: string | undefined, nextUnit: string) => {
  const parsedValue = Number(value)

  if (Number.isNaN(parsedValue) || currentUnit === nextUnit) return value

  const convertedValue = nextUnit === "C" ? ((parsedValue - 32) * 5) / 9 : parsedValue * 1.8 + 32

  const range = getTemperatureRange(nextUnit)

  return formatTemperatureValue(Math.max(range.min, Math.min(range.max, convertedValue)), nextUnit)
}

export const getInitialDraftForVariant = (variant: AlertRuleVariant): AlertRuleDraft => {
  switch (variant) {
    case "highTemperature":
      return { thresholdValue: "100", thresholdUnit: "F", durationValue: "15", durationUnit: "min" }
    case "lowTemperature":
      return { thresholdValue: "40", thresholdUnit: "F", durationValue: "15", durationUnit: "min" }
    case "highHumidity":
      return { thresholdValue: "80", thresholdUnit: "%", durationValue: "15", durationUnit: "min" }
    case "lowHumidity":
      return { thresholdValue: "20", thresholdUnit: "%", durationValue: "15", durationUnit: "min" }
    case "airQuality":
      return { thresholdValue: "10", durationValue: "15", durationUnit: "min" }
    case "coolingEfficiency":
    case "heatingEfficiency":
      return { thresholdValue: "4", durationValue: "4", durationUnit: "hour" }
  }
}

export const alertRuleModalConfigs: Record<AlertRuleVariant, AlertRuleModalConfig> = {
  highTemperature: {
    title: "High temperature detected",
    initialDraft: getInitialDraftForVariant("highTemperature"),
    renderSentence: (draft, updateDraft, thresholdError) => (
      <>
        If temperature is more than
        <AlertRuleValueUnitInput
          ariaLabel="Temperature threshold"
          value={draft.thresholdValue}
          onChange={value => updateDraft({ thresholdValue: value })}
          unit={draft.thresholdUnit || "F"}
          onUnitChange={value =>
            updateDraft({
              thresholdUnit: value,
              thresholdValue: convertTemperatureUnit(draft.thresholdValue, draft.thresholdUnit || "F", value),
            })
          }
          options={["F", "C"]}
          roundValue={draft.thresholdUnit !== "C"}
          {...getTemperatureRange(draft.thresholdUnit)}
          errorMessage={thresholdError}
          renderError={false}
        />
        for more than
        <AlertRuleValueUnitInput
          ariaLabel="Duration"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["min", "hour"]}
          min={0}
          max={100}
        />
      </>
    ),
  },
  lowTemperature: {
    title: "Low temperature detected",
    initialDraft: getInitialDraftForVariant("lowTemperature"),
    renderSentence: (draft, updateDraft, thresholdError) => (
      <>
        If temperature is less than
        <AlertRuleValueUnitInput
          ariaLabel="Temperature threshold"
          value={draft.thresholdValue}
          onChange={value => updateDraft({ thresholdValue: value })}
          unit={draft.thresholdUnit || "F"}
          onUnitChange={value =>
            updateDraft({
              thresholdUnit: value,
              thresholdValue: convertTemperatureUnit(draft.thresholdValue, draft.thresholdUnit || "F", value),
            })
          }
          options={["F", "C"]}
          roundValue={draft.thresholdUnit !== "C"}
          {...getTemperatureRange(draft.thresholdUnit)}
          errorMessage={thresholdError}
          renderError={false}
        />
        for more than
        <AlertRuleValueUnitInput
          ariaLabel="Duration"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["min", "hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
  },
  highHumidity: {
    title: "High humidity detected",
    initialDraft: getInitialDraftForVariant("highHumidity"),
    renderSentence: (draft, updateDraft, thresholdError) => (
      <>
        If humidity is more than
        <AlertRuleValueUnitInput
          ariaLabel="Humidity threshold"
          value={draft.thresholdValue}
          onChange={value => updateDraft({ thresholdValue: value })}
          unit={draft.thresholdUnit || "%"}
          onUnitChange={value => updateDraft({ thresholdUnit: value })}
          options={["%"]}
          min={0}
          max={1000}
          errorMessage={thresholdError}
          renderError={false}
        />
        for more than
        <AlertRuleValueUnitInput
          ariaLabel="Duration"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["min", "hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
  },
  lowHumidity: {
    title: "Low humidity detected",
    initialDraft: getInitialDraftForVariant("lowHumidity"),
    renderSentence: (draft, updateDraft, thresholdError) => (
      <>
        If humidity is less than
        <AlertRuleValueUnitInput
          ariaLabel="Humidity threshold"
          value={draft.thresholdValue}
          onChange={value => updateDraft({ thresholdValue: value })}
          unit={draft.thresholdUnit || "%"}
          onUnitChange={value => updateDraft({ thresholdUnit: value })}
          options={["%"]}
          min={0}
          max={1000}
          errorMessage={thresholdError}
          renderError={false}
        />
        for more than
        <AlertRuleValueUnitInput
          ariaLabel="Duration"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["min", "hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
  },
  airQuality: {
    title: "Air quality",
    initialDraft: getInitialDraftForVariant("airQuality"),
    renderSentence: (draft, updateDraft) => (
      <>
        If air quality TVOC readings more than
        <AlertRuleValueUnitInput
          ariaLabel="TVOC threshold"
          value={draft.thresholdValue}
          onChange={value => updateDraft({ thresholdValue: value })}
        />
        for more than
        <AlertRuleValueUnitInput
          ariaLabel="Duration"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["min", "hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
    info: [
      "If the air quality sensor reading is between 0 and 1, the air quality is considered Good.",
      "A reading between 1 and 3 indicates Fair air quality.",
      "Readings from 3 to 10 are classified as Poor, and any reading above 10 is considered Bad.",
    ],
  },
  coolingEfficiency: {
    title: "System efficiency for cooling",
    initialDraft: getInitialDraftForVariant("coolingEfficiency"),
    renderSentence: (draft, updateDraft) => (
      <>
        If indoor temperature is rising or shows no downward trend within
        <AlertRuleValueUnitInput
          ariaLabel="Evaluation window"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
  },
  heatingEfficiency: {
    title: "System efficiency for heating",
    initialDraft: getInitialDraftForVariant("heatingEfficiency"),
    renderSentence: (draft, updateDraft) => (
      <>
        If indoor temperature is falling or shows no upward trend within
        <AlertRuleValueUnitInput
          ariaLabel="Evaluation window"
          value={draft.durationValue}
          onChange={value => updateDraft({ durationValue: value })}
          unit={draft.durationUnit}
          onUnitChange={value => updateDraft({ durationUnit: value })}
          options={["hour"]}
          min={0}
          max={1000}
        />
      </>
    ),
  },
}

export { commonThresholdDescription }
